import { createContext, useContext, useMemo, useState } from 'react'
import type {
  CheckoutAddressOption,
  CheckoutOrderResult,
  CheckoutPaymentOption,
  CheckoutShippingOption,
  CheckoutSimulationResult,
} from '@/lib/api'

type ResourceStatus = 'idle' | 'loading' | 'ready' | 'pending' | 'error'

export type AddressState = {
  selectedId: number | null
  options: CheckoutAddressOption[]
  status: ResourceStatus
  error?: string
}

export type ShippingState = {
  mode: 'cif' | 'fob'
  selectedId: string | null
  selectionMode: 'manual' | 'automatic' | null
  options: CheckoutShippingOption[]
  status: ResourceStatus
  error?: string
}

export type PaymentState = {
  selectedId: string | null
  selectionMode: 'manual' | 'automatic' | null
  options: CheckoutPaymentOption[]
  status: ResourceStatus
  error?: string
}

export type FinancialState = {
  couponCode: string
  useCashback: boolean
  summary: CheckoutSimulationResult | null
  status: ResourceStatus
  error?: string
}

export const resolveFinancialStateForPaymentSelection = (
  current: FinancialState,
  paymentId: string | null
): FinancialState => paymentId
  // Forma e condição de pagamento não alteram a base tributária. Preservamos
  // integralmente o resumo fiscal já validado e mudamos apenas o pagamento.
  ? current
  : {
      ...current,
      summary: null,
      status: 'idle',
      error: undefined,
    }

export const resolveFinancialStateForShippingModeChange = (
  current: FinancialState
): FinancialState => current.summary
  // A troca CIF/FOB muda a base tributária, mas não deve apagar o último
  // resultado enquanto o backend calcula o novo. O resumo fica visível como
  // transição e é substituído pela simulação compatível com a nova modalidade.
  ? {
      ...current,
      status: 'loading',
      error: undefined,
    }
  : {
      ...current,
      status: 'idle',
      error: undefined,
    }

export const resolveFinancialStateForShippingSelection = (
  current: FinancialState,
  shippingMode: ShippingState['mode']
): FinancialState => shippingMode === 'fob'
  ? current
  : {
      ...current,
      summary: null,
      status: 'idle',
      error: undefined,
    }

export const resolveFinancialStateForShippingOptionsUpdate = (
  current: FinancialState,
  shippingMode: ShippingState['mode'],
  selectionChanged: boolean
): FinancialState => selectionChanged
  ? resolveFinancialStateForShippingSelection(current, shippingMode)
  : current

export type OrderState = {
  status: 'idle' | 'submitting' | 'success' | 'error'
  error?: string
  result: CheckoutOrderResult | null
}

export type CheckoutState = {
  address: AddressState
  shipping: ShippingState
  payment: PaymentState
  financial: FinancialState
  order: OrderState
}

export type CheckoutDraftState = {
  addressSelectedId: number | null
  shippingMode: ShippingState['mode']
  shippingSelectedId: string | null
  shippingSelectionMode?: ShippingState['selectionMode']
  paymentSelectedId: string | null
  paymentSelectionMode?: PaymentState['selectionMode']
  couponCode: string
  useCashback: boolean
}

type SetShippingOptionsBehavior = {
  preserveCurrentSelection?: boolean
  autoSelectFirst?: boolean
}

const defaultState: CheckoutState = {
  address: {
    selectedId: null,
    options: [],
    status: 'idle',
    error: undefined,
  },
  shipping: {
    mode: 'cif',
    selectedId: null,
    selectionMode: null,
    options: [],
    status: 'idle',
    error: undefined,
  },
  payment: {
    selectedId: null,
    selectionMode: null,
    options: [],
    status: 'idle',
    error: undefined,
  },
  financial: {
    couponCode: '',
    useCashback: false,
    summary: null,
    status: 'idle',
    error: undefined,
  },
  order: {
    status: 'idle',
    error: undefined,
    result: null,
  },
}

const normalizeShippingOptionToken = (value: string | null | undefined) => (value ?? '')
  .trim()
  .toLocaleLowerCase('pt-BR')

const shippingOptionPriceCents = (option: CheckoutShippingOption) => Math.round(Math.max(0, option.price) * 100)

const shippingOptionOriginalPriceCents = (option: CheckoutShippingOption) => (
  Math.round(Math.max(0, option.originalPrice) * 100)
)

export const didCheckoutShippingSelectionChange = (params: {
  currentSelectedId: string | null
  nextSelectedId: string | null
  currentSelectedOption: CheckoutShippingOption | null
  nextSelectedOption: CheckoutShippingOption | null
}) => {
  if (params.currentSelectedId !== params.nextSelectedId) return true
  if (!params.currentSelectedOption || !params.nextSelectedOption) return false

  return (
    shippingOptionPriceCents(params.currentSelectedOption)
      !== shippingOptionPriceCents(params.nextSelectedOption)
    || shippingOptionOriginalPriceCents(params.currentSelectedOption)
      !== shippingOptionOriginalPriceCents(params.nextSelectedOption)
    || normalizeShippingOptionToken(params.currentSelectedOption.quoteNumber)
      !== normalizeShippingOptionToken(params.nextSelectedOption.quoteNumber)
  )
}

const findEquivalentShippingOption = (
  options: CheckoutShippingOption[],
  selectedOption: CheckoutShippingOption
) => {
  const sameCarrierOptions = options.filter((option) => (
    option.transportadoraId === selectedOption.transportadoraId
    && option.tipoFrete === selectedOption.tipoFrete
  ))

  if (sameCarrierOptions.length === 0) return null

  const selectedQuoteNumber = normalizeShippingOptionToken(selectedOption.quoteNumber)
  if (selectedQuoteNumber) {
    const sameQuote = sameCarrierOptions.find(
      (option) => normalizeShippingOptionToken(option.quoteNumber) === selectedQuoteNumber
    )
    if (sameQuote) return sameQuote
  }

  if (selectedOption.cotacaoId > 0) {
    const sameCotacao = sameCarrierOptions.find((option) => option.cotacaoId === selectedOption.cotacaoId)
    if (sameCotacao) return sameCotacao
  }

  const selectedDescription = normalizeShippingOptionToken(selectedOption.description)
  if (selectedDescription) {
    const sameService = sameCarrierOptions.find(
      (option) => normalizeShippingOptionToken(option.description) === selectedDescription
    )
    if (sameService) return sameService
  }

  const selectedEta = normalizeShippingOptionToken(selectedOption.eta)
  const selectedPrice = shippingOptionPriceCents(selectedOption)
  const samePriceAndEta = sameCarrierOptions.find((option) => (
    shippingOptionPriceCents(option) === selectedPrice
    && normalizeShippingOptionToken(option.eta) === selectedEta
  ))
  if (samePriceAndEta) return samePriceAndEta

  return sameCarrierOptions.length === 1 ? sameCarrierOptions[0] : null
}

type CheckoutContextValue = {
  state: CheckoutState
  hydrateCheckoutDraft: (draft: Partial<CheckoutDraftState>) => void
  setAddressStatus: (status: AddressState['status'], error?: string) => void
  setAddressOptions: (options: CheckoutAddressOption[], preferredId?: number | null) => void
  selectAddress: (id: number | null) => void
  setShippingStatus: (status: ShippingState['status'], error?: string) => void
  setShippingMode: (mode: ShippingState['mode']) => void
  setShippingOptions: (options: CheckoutShippingOption[], behavior?: SetShippingOptionsBehavior) => void
  selectShippingOption: (
    id: string | null,
    options?: { selectionMode?: ShippingState['selectionMode'] }
  ) => void
  setPaymentStatus: (status: PaymentState['status'], error?: string) => void
  setPaymentOptions: (options: CheckoutPaymentOption[]) => void
  selectPaymentMethod: (
    id: string | null,
    options?: { selectionMode?: PaymentState['selectionMode'] }
  ) => void
  setCouponCode: (code: string) => void
  setUseCashback: (value: boolean) => void
  setFinancialStatus: (status: FinancialState['status'], error?: string) => void
  setFinancialSummary: (summary: CheckoutSimulationResult | null) => void
  setOrderState: (order: Partial<OrderState>) => void
  resetCheckout: () => void
}

const CheckoutContext = createContext<CheckoutContextValue | undefined>(undefined)

export function CheckoutProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<CheckoutState>(defaultState)

  const actions = useMemo<Omit<CheckoutContextValue, 'state'>>(() => ({
    hydrateCheckoutDraft: (draft) => {
      setState((current) => {
        const nextAddressSelectedId = Object.prototype.hasOwnProperty.call(draft, 'addressSelectedId')
          ? draft.addressSelectedId ?? null
          : current.address.selectedId
        const nextShippingMode = Object.prototype.hasOwnProperty.call(draft, 'shippingMode')
          ? (draft.shippingMode === 'fob' ? 'fob' : 'cif')
          : current.shipping.mode
        const nextShippingSelectedId = Object.prototype.hasOwnProperty.call(draft, 'shippingSelectedId')
          ? draft.shippingSelectedId ?? null
          : current.shipping.selectedId
        const nextShippingSelectionMode = Object.prototype.hasOwnProperty.call(draft, 'shippingSelectionMode')
          ? (draft.shippingSelectionMode === 'manual' || draft.shippingSelectionMode === 'automatic'
              ? draft.shippingSelectionMode
              : null)
          : (nextShippingSelectedId ? current.shipping.selectionMode ?? 'automatic' : null)
        const nextPaymentSelectedId = Object.prototype.hasOwnProperty.call(draft, 'paymentSelectedId')
          ? draft.paymentSelectedId ?? null
          : current.payment.selectedId
        const nextPaymentSelectionMode = Object.prototype.hasOwnProperty.call(draft, 'paymentSelectionMode')
          ? (draft.paymentSelectionMode === 'manual' || draft.paymentSelectionMode === 'automatic'
              ? draft.paymentSelectionMode
              : null)
          : current.payment.selectionMode
        const nextCouponCode = Object.prototype.hasOwnProperty.call(draft, 'couponCode')
          ? String(draft.couponCode ?? '').trim().toUpperCase()
          : current.financial.couponCode
        const nextUseCashback = Object.prototype.hasOwnProperty.call(draft, 'useCashback')
          ? Boolean(draft.useCashback)
          : current.financial.useCashback

        return {
          ...current,
          address: {
            ...current.address,
            selectedId: nextAddressSelectedId,
          },
          shipping: {
            ...current.shipping,
            mode: nextShippingMode,
            selectedId: nextShippingSelectedId,
            selectionMode: nextShippingSelectedId ? nextShippingSelectionMode : null,
          },
          payment: {
            ...current.payment,
            selectedId: nextPaymentSelectedId,
            selectionMode: nextPaymentSelectedId ? nextPaymentSelectionMode : null,
          },
          financial: {
            ...current.financial,
            couponCode: nextCouponCode,
            useCashback: nextUseCashback,
            summary: null,
            status: 'idle',
            error: undefined,
          },
        }
      })
    },
    setAddressStatus: (status, error) => {
      setState((current) => ({
        ...current,
        address: {
          ...current.address,
          status,
          error,
        },
      }))
    },
    setAddressOptions: (options, preferredId) => {
      setState((current) => {
        const normalizedOptions = Array.isArray(options) ? options : []
        const fallbackSelectedId = normalizedOptions.find((item) => item.isDefault)?.id
          ?? normalizedOptions.find((item) => item.id > 0)?.id
          ?? normalizedOptions[0]?.id
          ?? null
        const selectedId = normalizedOptions.some((item) => item.id === current.address.selectedId)
          ? current.address.selectedId
          : normalizedOptions.some((item) => item.id === preferredId)
            ? preferredId ?? null
            : fallbackSelectedId

        return {
          ...current,
          address: {
            ...current.address,
            options: normalizedOptions,
            selectedId,
            status: 'ready',
            error: undefined,
          },
        }
      })
    },
    selectAddress: (id) => {
      setState((current) => ({
        ...current,
        address: {
          ...current.address,
          selectedId: id,
        },
        shipping: {
          ...current.shipping,
          selectedId: null,
          options: [],
          status: 'idle',
          error: undefined,
        },
        financial: {
          ...current.financial,
          summary: null,
          status: 'idle',
          error: undefined,
        },
      }))
    },
    setShippingStatus: (status, error) => {
      setState((current) => ({
        ...current,
        shipping: {
          ...current.shipping,
          status,
          error,
        },
      }))
    },
    setShippingMode: (mode) => {
      setState((current) => {
        if (current.shipping.mode === mode) {
          return current
        }

        return {
          ...current,
          shipping: {
            ...current.shipping,
            mode,
            selectedId: null,
            selectionMode: null,
            options: [],
            status: 'idle',
            error: undefined,
          },
          financial: {
            ...resolveFinancialStateForShippingModeChange(current.financial),
          },
        }
      })
    },
    setShippingOptions: (options, behavior) => {
      setState((current) => {
        const normalizedOptions = Array.isArray(options) ? options : []
        const currentSelectedOption = current.shipping.options.find((item) => item.id === current.shipping.selectedId) ?? null
        const exactSelectedOption = normalizedOptions.find((item) => item.id === current.shipping.selectedId) ?? null
        const equivalentSelectedOption = !exactSelectedOption && currentSelectedOption
          ? findEquivalentShippingOption(normalizedOptions, currentSelectedOption)
          : null
        const shouldPreserveCurrentOptions = Boolean(
          behavior?.preserveCurrentSelection
          && normalizedOptions.length === 0
          && currentSelectedOption
        )
        const nextOptions = shouldPreserveCurrentOptions ? current.shipping.options : normalizedOptions
        const selectedOption = exactSelectedOption
          ?? equivalentSelectedOption
          ?? (shouldPreserveCurrentOptions ? currentSelectedOption : null)
        const selectedId = selectedOption?.id
          ?? (behavior?.autoSelectFirst === false ? null : (nextOptions[0]?.id ?? null))
        const selectionMode = selectedId
          ? (
              selectedId === current.shipping.selectedId || equivalentSelectedOption || shouldPreserveCurrentOptions
                ? current.shipping.selectionMode ?? 'automatic'
                : 'automatic'
            )
          : null
        const shippingSelectionChanged = didCheckoutShippingSelectionChange({
          currentSelectedId: current.shipping.selectedId,
          nextSelectedId: selectedId,
          currentSelectedOption,
          nextSelectedOption: selectedOption,
        })

        return {
          ...current,
          shipping: {
            ...current.shipping,
            options: nextOptions,
            selectedId,
            selectionMode,
            status: nextOptions.length > 0 ? 'ready' : 'idle',
            error: undefined,
          },
          // Uma lista recotada pode restaurar automaticamente a opção CIF que
          // o comprador já havia escolhido. Essa troca também muda a base
          // fiscal e precisa invalidar o resumo FOB anterior, mesmo sem passar
          // pelo clique explícito de selectShippingOption.
          financial: resolveFinancialStateForShippingOptionsUpdate(
            current.financial,
            current.shipping.mode,
            shippingSelectionChanged
          ),
        }
      })
    },
    selectShippingOption: (id, options) => {
      setState((current) => {
        const nextSelectionMode = id
          ? (options?.selectionMode === 'automatic' ? 'automatic' : 'manual')
          : null

        if (
          current.shipping.selectedId === id
          && current.shipping.selectionMode === nextSelectionMode
        ) {
          return current
        }

        return {
          ...current,
          shipping: {
            ...current.shipping,
            selectedId: id,
            selectionMode: nextSelectionMode,
          },
          // No FOB a transportadora é apenas uma escolha operacional do
          // comprador: não há valor de frete no pedido nem mudança da base
          // tributária. Mantemos o resumo fiscal já calculado. No CIF, como a
          // transportadora altera o frete, os totais precisam ser simulados.
          financial: resolveFinancialStateForShippingSelection(
            current.financial,
            current.shipping.mode
          ),
        }
      })
    },
    setPaymentStatus: (status, error) => {
      setState((current) => ({
        ...current,
        payment: {
          ...current.payment,
          status,
          error,
        },
      }))
    },
    setPaymentOptions: (options) => {
      setState((current) => {
        const normalizedOptions = Array.isArray(options) ? options : []
        const keepCurrent = normalizedOptions.some((item) => item.id === current.payment.selectedId)
        const pixDefault = normalizedOptions.find((item) => item.formaPagamentoId === 9)
        const selectedId = keepCurrent
          ? current.payment.selectedId
          : (pixDefault?.id ?? normalizedOptions[0]?.id ?? null)

        return {
          ...current,
          payment: {
            ...current.payment,
            options: normalizedOptions,
            selectedId,
            selectionMode: selectedId
              ? (keepCurrent ? current.payment.selectionMode : 'automatic')
              : null,
            status: 'ready',
            error: undefined,
          },
        }
      })
    },
    selectPaymentMethod: (id, options) => {
      setState((current) => {
        const nextSelectionMode = id
          ? (options?.selectionMode === 'manual' ? 'manual' : 'automatic')
          : null

        if (
          current.payment.selectedId === id
          && current.payment.selectionMode === nextSelectionMode
        ) {
          return current
        }

        const paymentOptionChanged = current.payment.selectedId !== id

        return {
          ...current,
          payment: {
            ...current.payment,
            selectedId: id,
            selectionMode: nextSelectionMode,
          },
          // Alterar apenas a origem da seleção (automática -> manual) não muda
          // a condição financeira e, portanto, não exige uma nova simulação.
          financial: paymentOptionChanged
            ? resolveFinancialStateForPaymentSelection(current.financial, id)
            : current.financial,
        }
      })
    },
    setCouponCode: (code) => {
      setState((current) => ({
        ...current,
        financial: {
          ...current.financial,
          couponCode: code.trim().toUpperCase(),
        },
      }))
    },
    setUseCashback: (value) => {
      setState((current) => ({
        ...current,
        financial: {
          ...current.financial,
          useCashback: value,
        },
      }))
    },
    setFinancialStatus: (status, error) => {
      setState((current) => ({
        ...current,
        financial: {
          ...current.financial,
          status,
          error,
        },
      }))
    },
    setFinancialSummary: (summary) => {
      setState((current) => ({
        ...current,
        financial: {
          ...current.financial,
          summary,
          status: summary ? 'ready' : 'idle',
          error: undefined,
        },
      }))
    },
    setOrderState: (order) => {
      setState((current) => ({
        ...current,
        order: {
          ...current.order,
          ...order,
        },
      }))
    },
    resetCheckout: () => {
      setState(defaultState)
    },
  }), [])
  const value = useMemo<CheckoutContextValue>(() => ({
    state,
    ...actions,
  }), [actions, state])

  return <CheckoutContext.Provider value={value}>{children}</CheckoutContext.Provider>
}

export function useCheckout() {
  const context = useContext(CheckoutContext)
  if (!context) {
    throw new Error('useCheckout must be used within CheckoutProvider')
  }
  return context
}
